'use client';

import React, { useEffect, useState } from 'react';
import { useChatStore } from '@/store/chatStore';

export function useMatchSeconds(): number {
  const status = useChatStore((s) => s.status);
  const [s, setS] = useState(0);
  useEffect(() => {
    if (status !== 'connected') {
      setS(0);
      return;
    }
    setS(0);
    const id = setInterval(() => setS((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [status]);
  return s;
}

interface ChatContextRailProps {
  matchSeconds: number;
  isConnected: boolean;
}

const HOUSE_RULES = [
  'Be kind. The stranger is a person.',
  "Don't share personal info.",
  '18+ content goes to /adult.',
  'Report anything off. It helps.',
];

function useLiveCounter(base: number, rate: number) {
  const [n, setN] = useState(base);
  useEffect(() => {
    const id = setInterval(() => {
      setN((x) => x + Math.max(1, Math.round(Math.random() * rate)));
    }, 1400);
    return () => clearInterval(id);
  }, [rate]);
  return n;
}

export function ChatContextRail({ matchSeconds, isConnected }: ChatContextRailProps) {
  const { messages, strangerDisplayName, strangerGender, lifetimeConnections } = useChatStore();
  const online = useLiveCounter(2847, 3);
  const realMessages = messages.filter((m) => m.type !== 'SYSTEM').length;
  const barPct = 40 + (online % 50);

  const genderLabel =
    strangerGender === 'FEMALE' ? 'Female' : strangerGender === 'MALE' ? 'Male' : strangerGender === 'OTHER' ? 'Other' : '—';

  return (
    <aside className="bc-ctx-rail">
      <div className="bc-ctx-card">
        <div className="bc-ctx-label">
          <span className="num">§A</span> · Session
        </div>
        <div className="bc-ctx-big tnum">#{lifetimeConnections.toLocaleString('en-US')}</div>
        <div className="bc-ctx-sub">Your lifetime connection count</div>
      </div>

      <div className="bc-ctx-card">
        <div className="bc-ctx-label">
          <span className="num">§B</span> · This stranger
        </div>
        <div className="bc-ctx-rows">
          <div>
            <span>Name</span>
            <b>{isConnected ? (strangerDisplayName || 'Stranger') : '—'}</b>
          </div>
          <div>
            <span>Gender</span>
            <b>{isConnected ? genderLabel : '—'}</b>
          </div>
          <div>
            <span>Messages</span>
            <b className="tnum">{realMessages}</b>
          </div>
          <div>
            <span>Status</span>
            <b style={{ color: isConnected ? 'var(--bsc-accent)' : 'var(--bsc-fg-3)' }}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </b>
          </div>
        </div>
      </div>

      <div className="bc-ctx-card">
        <div className="bc-ctx-label">
          <span className="num">§C</span> · Live network
        </div>
        <div className="bc-ctx-big tnum">{online.toLocaleString('en-US')}</div>
        <div className="bc-ctx-sub">Strangers online right now</div>
        <div className="bc-ctx-bar">
          <div className="bc-ctx-bar-fill" style={{ width: `${barPct}%` }} />
        </div>
      </div>

      <div className="bc-ctx-card bc-ctx-rules">
        <div className="bc-ctx-label">
          <span className="num">§D</span> · House rules
        </div>
        <ol>
          {HOUSE_RULES.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ol>
      </div>

      <div className="bc-ctx-footer">
        <span>Stranger World</span>
        <span>v7.2 · zero logs</span>
      </div>
    </aside>
  );
}
